<template>
  <div class="the-aside-left">
    <nav class="the-aside-left__nav">
      <div
        v-for="(value, name) in visibleMenuItems"
        :key="name"
        :class="[
          'the-aside-left__nav-menu',
          { 'the-aside-left__nav-menu--bottom': isSettingsMenu(name) }
        ]"
      >
        <h2
          v-if="!isSettingsMenu(name)"
          class="the-aside-left__nav-menu-title"
        >
          {{ name }}
        </h2>
        <template v-for="(library, index) in value">
          <router-link
            :key="`${name}-library-${index}`"
            :to="{ path: library.link }"
            :class="[
              'the-aside-left__nav-menu-item',
              {
                'the-aside-left__nav-menu-item--settings': isSettingsMenu(name)
              }
            ]"
          >
            <i
              :class="[
                'the-aside-left__nav-menu-item-icon',
                {
                  'the-aside-left__nav-menu-item-icon--faq':
                    library.link === '/faq'
                }
              ]"
              :style="{
                webkitMask: `url('${library.icon}') no-repeat center`,
                mask: `url('${library.icon}') no-repeat center`,
                maskSize: 'contain',
                webkitMaskSize: 'contain'
              }"
            />
            {{ library.title }}
            <i
              class="el-icon-arrow-right the-aside-left__nav-menu-item-arrow-icon"
            />
          </router-link>
        </template>
      </div>
    </nav>
    <div class="the-aside-left-version">
      Build ID: {{ version }}
    </div>
  </div>
</template>

<script>
// canUserAccessStitchPlugin
import FeatureFlags from '@/services/featureFlags'
import { canUserAccessUserManagement } from '@/services/permissions'
import { mapGetters } from 'vuex'

export default {
  name: 'TheAsideLeft',

  data () {
    return {
      version: process.env.SENTRY_RELEASE
    }
  },

  computed: {
    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {Array}
     */
    menuItems () {
      return [
        {
          name: 'Libraries',
          show: () => true,
          items: [
            {
              title: 'Blocks',
              link: '/library/blocks',
              icon: require('@/assets/icons/block.svg')
            },
            {
              title: 'Trims',
              link: '/library/trims',
              icon: require('@/assets/icons/trim.svg')
            },
            {
              title: 'Fabrics',
              link: '/library/fabrics',
              icon: require('@/assets/icons/fabric.svg')
            },
            {
              title: 'Stitches',
              link: '/library/stitches',
              icon: require('@/assets/icons/stitch.svg')
            },
            {
              title: 'Colors',
              link: '/library/colors',
              icon: require('@/assets/icons/colours.svg')
            }
          ]
        },
        {
          name: 'Workspaces',
          items: [
            {
              title: 'My Watchlist',
              link: '/watchlist',
              icon: require('@/assets/icons/star.svg'),
              show: () => true
            },
            {
              title: 'My Renders',
              link: '/jobs',
              icon: require('@/assets/icons/render.svg'),
              show: () => true
            },
            {
              title: 'Styles',
              link: '/styles',
              icon: require('@/assets/icons/style.svg'),
              show: () => true
            }
          ]
        },
        {
          name: 'Settings',
          items: [
            {
              title: 'Admin Tools',
              link: '/admin-tools',
              icon: require('@/assets/icons/physics.svg'),
              show: () => canUserAccessUserManagement()
            },
            {
              title: 'Stitch Plugin',
              link: '/plugin',
              icon: require('@/assets/icons/plugin.svg'),
              show: FeatureFlags.canUserAccessStitchPlugin
            },
            {
              title: 'FAQs',
              link: '/faq',
              icon: require('@/assets/icons/faq.png'),
              show: () => true
            }
          ]
        }
      ]
    },

    /**
     * @returns {Array}
     */
    visibleMenuItems () {
      const menuGroups = this.menuItems.filter(
        group => group.show === undefined || group.show()
      )

      return menuGroups.reduce(
        (accumulator, value) => ({
          ...accumulator,
          [value.name]: value.items.filter(
            item => item.show === undefined || item.show()
          )
        }),
        {}
      )
    }
  },

  methods: {
    /**
     * @param   {string}  menuName
     *
     * @returns {boolean}
     */
    isSettingsMenu (menuName) {
      return menuName.toLowerCase() === 'settings'
    }
  }
}
</script>

<style lang="scss" scoped>
$menu-icon-size: spacing(4);

.the-aside-left {
  width: $aside-width;
  padding: spacing(2) spacing(4) spacing(4) spacing(2);
  font-family: $primary-font;
}

.the-aside-left__nav {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: spacing(2);
  border: $border-divider;
  border-radius: spacing(1/2);
  box-shadow: $shadow-default;
}

.the-aside-left__nav-menu-item-icon {
  width: $menu-icon-size;
  height: $menu-icon-size;
  margin-right: spacing(1/2);
  background-color: $font-color-default;
  transition: background-color $transition;
}

.the-aside-left__nav-menu-item-arrow-icon {
  position: absolute;
  right: 0;
}

.the-aside-left__nav-menu-item {
  position: relative;
  display: flex;
  align-items: center;
  padding: spacing(1/2) 0;
  color: $grey-dark;
  border-top: $border-divider;
  transition: color $transition;

  &:hover {
    color: $font-color-active;
  }

  &:last-of-type {
    margin-bottom: spacing(2);
    border-bottom: $border-divider;
  }
}

.the-aside-left .the-aside-left__nav-menu-item--settings {
  border-top: 0;
  border-bottom: 0;
}

.the-aside-left__nav-menu {
  display: flex;
  flex-direction: column;
  margin-bottom: spacing(2);
}

.the-aside-left__nav-menu--bottom {
  margin-top: auto;
  margin-bottom: 0;

  .the-aside-left__nav-menu-item {
    margin-bottom: 0;
  }

  .the-aside-left__nav-menu-item-arrow-icon {
    display: none;
  }
}

.the-aside-left__nav-menu-title {
  margin-bottom: spacing(1);
  text-transform: capitalize;
}

.router-link-active {
  color: $font-color-active;

  .the-aside-left__nav-menu-item-icon {
    background-color: $font-color-active;
  }
}

.the-aside-left-version {
  @include text-debug;

  position: absolute;
  bottom: 0;
  opacity: 0.5;
}
</style>
